/**
 * Transform API data to match component expectations
 */

import { isMongoObjectId, safeDisplayName } from './slugUtils';

// Transform article from API to component format
export const transformArticle = (article) => {
  if (!article) return null;

  const authorObj = article.author && typeof article.author === 'object' ? article.author : null;
  const categoryObj = article.category && typeof article.category === 'object' ? article.category : null;

  const authorName = safeDisplayName(
    authorObj?.name || (typeof article.author === 'string' ? article.author : ''),
    ''
  );
  const authorSlug = authorObj?.slug || article.authorSlug || null;
  const authorAvatar = authorObj?.avatar || article.authorAvatar || null;

  const categoryName = safeDisplayName(
    categoryObj?.name || (typeof article.category === 'string' ? article.category : ''),
    ''
  );
  const categorySlug = categoryObj?.slug || null;
  const categoryImageUrl = categoryObj?.imageUrl || article.categoryImageUrl || '';

  return {
    title: article.title,
    excerpt: article.excerpt,
    category: categoryName,
    categorySlug,
    categoryImageUrl,
    categoryColor: categoryObj?.color || article.category?.color || 'teal',
    author: authorName,
    authorSlug,
    authorAvatar,
    date: article.publishedAt ? new Date(article.publishedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }) : article.date,
    publishedAt: article.publishedAt,
    updatedAt: article.updatedAt,
    readTime: article.readTime || '5 min read',
    imageUrl: article.imageUrl,
    imageAlt: article.imageAlt || '',
    slug: article.slug,
    _id: article._id,
    views: article.views || 0,
    likes: article.likes || 0,
    tags: article.tags || [],
    content: article.content || [],
    commentCount: article.commentCount || 0,
    language: article.language || null,
    // Per-language SEO block (metaTitle, metaDescription, keywords, focusKeyword,
    // canonicalUrl, robots, ogImage). Required for meta tags to reflect edits.
    seo: article.seo
      ? {
          ...article.seo,
          keywords: Array.isArray(article.seo.keywords)
            ? article.seo.keywords.map((k) => String(k || '').trim()).filter(Boolean)
            : typeof article.seo.keywords === 'string'
              ? article.seo.keywords.split(',').map((k) => k.trim()).filter(Boolean)
              : [],
          focusKeyword: article.seo.focusKeyword || '',
          metaTitle: article.seo.metaTitle || '',
          metaDescription: article.seo.metaDescription || '',
        }
      : null,
    twitterCard: article.twitterCard || 'summary_large_image',
    articleSchema: article.articleSchema || null,
    offers: Array.isArray(article.offers) ? article.offers : [],
  };
};

// Transform category from API to component format
export const transformCategory = (category) => {
  if (!category) return null;

  const name = safeDisplayName(category.name, '');
  if (!name && isMongoObjectId(category.slug)) return null;
  
  return {
    name: name || 'Category',
    color: category.color,
    count: category.postCount || 0,
    postCount: category.postCount || 0,
    imageUrl: category.imageUrl,
    slug: category.slug,
    description: category.description,
    seo: category.seo || null,
  };
};

// Transform author from API to component format
export const transformAuthor = (author) => {
  if (!author) return null;

  const name = safeDisplayName(author.name, '');
  if (!name && isMongoObjectId(author.slug || author.baseSlug)) return null;
  
  return {
    name: name || 'Author',
    slug: author.slug || author.baseSlug,
    bio: author.bio,
    avatar: author.avatar,
    email: author.email,
    socialLinks: author.socialLinks || {},
    customLinks: author.customLinks || [],
    articleCount: author.articleCount || 0,
    totalViews: author.totalViews || 0,
    createdAt: author.createdAt || null,
    seo: author.seo || null,
  };
};

// Transform array of articles
export const transformArticles = (articles) => {
  if (!Array.isArray(articles)) return [];
  return articles.map(transformArticle).filter(Boolean);
};

// Transform array of categories
export const transformCategories = (categories) => {
  if (!Array.isArray(categories)) return [];
  return categories.map(transformCategory).filter(Boolean);
};

// Transform array of authors
export const transformAuthors = (authors) => {
  if (!Array.isArray(authors)) return [];
  return authors.map(transformAuthor).filter(Boolean);
};


